<template>
  <div class="checkout-container">
    <!-- Thank You Page -->
    <div v-if="thankYouPage" class="thank-you-section">
      <div class="success-icon">
        <b-icon icon="check-circle-fill" font-scale="4"></b-icon>
      </div>
      <h2 class="thank-you-title">{{ $t('checkout.thankYou') }}</h2>
      <p class="thank-you-message">
        {{ $t('checkout.thankYouMessage') }}
      </p>
      <b-button 
        v-if="user" 
        variant="primary" 
        to="/admin/dashboard" 
        class="mt-4"
        size="lg"
      >
        {{ $t('backToPlatform') }}
      </b-button>
      <b-button 
        v-else 
        variant="primary" 
        to="/login" 
        class="mt-4"
        size="lg"
      >
        {{ $t('signIn') }}
      </b-button>
    </div>

    <!-- Checkout Page -->
    <div v-else class="checkout-content">
      <div class="checkout-header">
        <h2>{{ $t('checkout.title') }}</h2>
        <p class="checkout-subtitle">{{ $t('checkout.subtitle') }}</p>
      </div>

      <!-- Order Summary -->
      <div class="checkout-card summary-card-centered">
        <h4 class="card-section-title">{{ $t('checkout.orderSummary') }}</h4>
        
        <!-- Items List -->
        <div class="order-items">
          <div 
            v-for="(name, idx) in prodNames" 
            :key="idx"
            class="order-item"
          >
            <div class="item-info">
              <div class="item-name">
                {{ name }}
                <span v-if="prodOptions[idx]" class="item-option">({{ prodOptions[idx] }})</span>
              </div>
              <div class="item-quantity">x{{ quantities[idx] }}</div>
            </div>
            <div class="item-price">{{ prices[idx] }} {{ CURRENCY }}</div>
          </div>
        </div>

        <!-- Total -->
        <div class="order-total">
          <span class="total-label">{{ $t('checkout.total') }}</span>
          <span class="total-amount">{{ HUMAN_TOTAL_AMOUNT }} {{ CURRENCY }}</span>
        </div>

        <!-- Prorated Price Notice -->
        <div v-if="IS_PRORATED" class="prorated-notice">
          <div class="prorated-header">
            <b-icon icon="info-circle-fill"></b-icon>
            <strong>{{ $t('checkout.proratedPrice') }}</strong>
          </div>
          <p class="prorated-explanation">
            {{ $t('checkout.proratedExplanation') }}
          </p>
        </div>

        <!-- Trial Period Notice -->
        <div v-if="TPERIOD !== null && TPERIOD !== '0' && TPERIOD !== ''" class="trial-notice">
          <b-icon icon="info-circle"></b-icon>
          <span>{{ $t('checkout.trialPeriodNotice', {days: TPERIOD}) }}</span>
        </div>

        <!-- Payment Method Type Selection (Card vs Bank) -->
        <div v-if="isCardAvailable && isBankAccountAvailable" class="payment-type-section">
          <h5 class="payment-method-title">{{ $t('checkout.selectPaymentType') }}</h5>
          <div class="payment-type-options">
            <div 
              class="payment-type-option"
              :class="{ 'selected': paymentMethod === 'card' }"
              @click="paymentMethod = 'card'"
            >
              <div class="option-icon">
                <b-icon icon="credit-card" font-scale="1.5"></b-icon>
              </div>
              <div class="option-content">
                <span class="option-title">{{ $t('checkout.creditCard') }}</span>
                <span class="option-description">{{ $t('checkout.creditCardDescription') }}</span>
              </div>
              <div v-if="paymentMethod === 'card'" class="option-check">
                <b-icon icon="check-circle-fill"></b-icon>
              </div>
            </div>
            <div 
              class="payment-type-option"
              :class="{ 'selected': paymentMethod === 'bankaccount' }"
              @click="paymentMethod = 'bankaccount'"
            >
              <div class="option-icon">
                <b-icon icon="bank" font-scale="1.5"></b-icon>
              </div>
              <div class="option-content">
                <span class="option-title">{{ $t('checkout.bankAccount') }}</span>
                <span class="option-description">{{ $t('checkout.bankAccountDescription') }}</span>
              </div>
              <div v-if="paymentMethod === 'bankaccount'" class="option-check">
                <b-icon icon="check-circle-fill"></b-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- Saved Payment Method -->
        <div v-if="paymentMethod === 'card' && paymentTokens && paymentTokens.length > 0" class="payment-method-section">
          <h5 class="payment-method-title">{{ $t('checkout.paymentMethod') }}</h5>
          <div
            v-for="token in paymentTokens"
            :key="token.uuid"
            class="saved-card"
            :class="{ 'selected': selectedPaymentToken === token.uuid }"
            @click="selectedPaymentToken = token.uuid"
          >
            <div class="card-icon">
              <b-icon :icon="getCardIcon(token.credit_card_name)" font-scale="1.5"></b-icon>
            </div>
            <div class="card-details">
              <span class="card-brand">{{ capitalize(token.credit_card_name) }}</span>
              <span class="card-number">{{ token.credit_card_number }}</span>
            </div>
            <div class="card-expiry">
              {{ formatExpiry(token.expires_at) }}
            </div>
            <div v-if="selectedPaymentToken === token.uuid" class="card-check">
              <b-icon icon="check-circle-fill"></b-icon>
            </div>
          </div>
          <div
            class="saved-card new-card-option"
            :class="{ 'selected': selectedPaymentToken === null }"
            @click="selectedPaymentToken = null"
          >
            <div class="card-icon">
              <b-icon icon="plus-circle" font-scale="1.5"></b-icon>
            </div>
            <div class="card-details">
              <span class="card-brand">{{ $t('checkout.useNewCard') }}</span>
              <span class="card-number">{{ $t('checkout.useNewCardDescription') }}</span>
            </div>
            <div v-if="selectedPaymentToken === null" class="card-check">
              <b-icon icon="check-circle-fill"></b-icon>
            </div>
          </div>
        </div>

        <!-- Complete Button -->
        <b-button 
          block 
          variant="primary" 
          @click="onSubmit"
          size="lg"
          class="complete-button"
        >
          {{ hasPaymentToken ? $t('checkout.completePayment') : $t('checkout.completeCheckout') }}
        </b-button>
        
        <p v-if="paymentMethod === 'card' && !hasPaymentToken" class="redirect-notice">{{ $t('checkout.redirectionNotice') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.checkout-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
}

// Thank You Page
.thank-you-section {
  text-align: center;
  padding: 80px 20px;
  max-width: 600px;
  margin: 0 auto;

  .success-icon {
    color: #10b981;
    margin-bottom: 32px;
  }

  .thank-you-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 16px;
    color: var(--text-primary);
  }

  .thank-you-message {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }
}

// Checkout Page
.checkout-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.checkout-header {
  text-align: center;
  margin-bottom: 48px;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 12px;
    color: var(--text-primary);
  }

  .checkout-subtitle {
    font-size: 1.1rem;
    color: var(--text-secondary);
  }
}

.summary-card-centered {
  max-width: 700px;
  margin: 0 auto;
}

.checkout-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);

  @media (max-width: 576px) {
    padding: 24px;
  }
}

.card-section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 24px;
  color: var(--text-primary);
}

// Form Styling
.modern-select,
.modern-input {
  background: $dark-hard2-background;
  border: 1px solid $dark-border2;
  color: var(--text-primary);
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    background: $dark-background;
    border-color: $protokol-royal-active;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
    color: var(--text-primary);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
}

.modern-select {
  option {
    background: $dark-hard2-background;
    color: var(--text-primary);
  }
}

.modern-checkbox {
  ::v-deep .custom-control-label {
    color: var(--text-primary);
    font-size: 1rem;
    
    &::before {
      background: $dark-hard2-background;
      border-color: $dark-border2;
    }
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: $protokol-royal-active;
    border-color: $protokol-royal-active;
  }
}

::v-deep .form-group {
  margin-bottom: 24px;

  label {
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
  }
}

.trial-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  color: #93c5fd;
  font-size: 0.95rem;
  margin-top: 24px;
}

.prorated-notice {
  padding: 16px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  margin-top: 16px;

  .prorated-header {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #c4b5fd;
    font-size: 1rem;
    margin-bottom: 8px;

    svg {
      flex-shrink: 0;
    }

    strong {
      font-weight: 600;
    }
  }

  .prorated-explanation {
    color: rgba(196, 181, 253, 0.85);
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;
    padding-left: 26px;
  }
}

// Order Summary

.order-items {
  margin-bottom: 24px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid $dark-border2;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }

  .item-info {
    flex: 1;
    
    .item-name {
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 4px;
      
      .item-option {
        color: var(--text-secondary);
        font-weight: 400;
        font-size: 0.9rem;
      }
    }

    .item-quantity {
      color: var(--text-secondary);
      font-size: 0.9rem;
    }
  }

  .item-price {
    font-weight: 600;
    color: $protokol-royal-active;
    white-space: nowrap;
    margin-left: 16px;
  }
}

.order-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-top: 2px solid $dark-border2;
  margin-bottom: 24px;

  .total-label {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .total-amount {
    font-size: 1.75rem;
    font-weight: 700;
    color: $protokol-royal-active;
  }
}

.complete-button {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 14px 24px;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
  }
}

.redirect-notice {
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
}

// Payment Method Section
.payment-type-section {
  margin: 24px 0;
  padding-top: 24px;
  border-top: 1px solid $dark-border2;
}

.payment-type-options {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.payment-type-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: $dark-hard2-background;
  border: 2px solid $dark-border2;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--text-muted);
  }

  &.selected {
    border-color: $protokol-royal-active;
    background: rgba(102, 126, 234, 0.1);
  }

  .option-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--border-default);
    border-radius: 8px;
    color: var(--text-primary);
  }

  .option-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .option-title {
      font-weight: 600;
      color: var(--text-primary);
      font-size: 1rem;
    }

    .option-description {
      color: var(--text-secondary);
      font-size: 0.85rem;
    }
  }

  .option-check {
    color: $protokol-royal-active;
    font-size: 1.25rem;
  }
}

.payment-method-section {
  margin: 24px 0;
  padding-top: 24px;
  border-top: 1px solid $dark-border2;
}

.payment-method-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
}

.saved-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: $dark-hard2-background;
  border: 2px solid $dark-border2;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: var(--text-muted);
  }

  &.selected {
    border-color: $protokol-royal-active;
    background: rgba(102, 126, 234, 0.1);
  }
}

.card-icon {
  width: 48px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--border-default);
  border-radius: 6px;
  color: var(--text-primary);
}

.card-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .card-brand {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  .card-number {
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-family: monospace;
  }
}

.card-expiry {
  color: var(--text-muted);
  font-size: 0.85rem;
}

.card-check {
  color: $protokol-royal-active;
  font-size: 1.25rem;
}
</style>

<script>
import Env from '@/plugins/protokol/lib/Env'
export default {
  name: "Index",
  data: function () {
    return {
      user: null,
      name: null,
      email: null,
      address: null,
      city: null,
      pib: null,
      nationalId: null,
      selected: null,
      prodNames: null,
      quantities: null,
      prices: null,
      total: null,
      prodOptions: null,
      PRODS: null,
      PROD_NAMES: null,
      GROUPS: null,
      PRICES: null,
      TPERIOD: null,
      PHASH: null,
      QTY: null,
      CURRENCY: null,
      TOTAL_AMOUNT: null,
      PROD_OPTIONS: null,
      options: [
        { value: 'company', text: 'Pravno Lice' },
        { value: 'person', text: 'Fizičko Lice' },
      ],
      thankYouPage: false,
      billingAddress: null,
      paymentMethod: 'invoice',
      showOtherFields: false,
      paymentTokens: [],
      selectedPaymentToken: null,
      IS_PRORATED: null,
      paymentMethod: 'card', // 'card' or 'bankaccount'
    }
  },
  watch: {
    'billingAddress.vat_number': function(n, o) {
        if (n.length == 9) {
          this.$integrationApi.get("/karadjordje/services/nbs/search?vatNumber=" + n).then((resp) => {
            let info = resp.data

            this.billingAddress.main_number = info.mb
            this.billingAddress.company = info.name
            this.billingAddress.address = info.address
            this.billingAddress.city = info.place
          }).catch(() => {
             if (o && o.length != 0) {
              this.$bvToast.toast('Nismo uspeli da pronadjemo podatke. Molimo vas popunite formu.', {
                title: 'Greška',
                variant: 'danger'
              })
            }
          })
          .finally(() => {
            this.showOtherFields = true
          })
        }
    }
  },
  computed: {
    hasPaymentToken() {
      return this.paymentTokens && this.paymentTokens.length > 0 && this.selectedPaymentToken !== null
    },

    availablePaymentMethods() {
      // Check if AVAILABLE_PAYMENT_METHODS is specified in query params
      const queryMethods = this.$route.query.AVAILABLE_PAYMENT_METHODS
      if (queryMethods) {
        // Split by comma and trim whitespace
        return queryMethods.split(',').map(m => m.trim())
      }
      // If not specified, return default (card only)
      return ['card']
    },

    isCardAvailable() {
      const cardPaymentsEnabled = Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
      return cardPaymentsEnabled && this.availablePaymentMethods.includes('card')
    },

    isBankAccountAvailable() {
      return this.availablePaymentMethods.includes('bankaccount')
    },

    allowBankPayment() {
      // Show bank payment option if it's in available methods
      // AND if project allows it (for backwards compatibility)
      const projectInfo = this.$store.getters.getProjectInfo
      const projectAllows = projectInfo && projectInfo.allow_bank_payment === true
      return this.isBankAccountAvailable && (this.availablePaymentMethods.length > 1 || projectAllows)
    },
  },
  methods: {
    getCardIcon(cardName) {
      const icons = {
        'visa': 'credit-card-2-front',
        'mastercard': 'credit-card-2-front',
        'maestro': 'credit-card-2-front',
        'amex': 'credit-card-2-front'
      }
      return icons[cardName.toLowerCase()] || 'credit-card-2-front'
    },
    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    formatExpiry(expiresAt) {
      if (!expiresAt) return ''
      const date = new Date(expiresAt)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      return month + '/' + year
    },
    onSubmit(e) {
      e.preventDefault()

      const popup = this.$loadingPopup()

      const params = { ...this.$route.query }
      if (this.hasPaymentToken && this.selectedPaymentToken) {
        params.DIRECTPAY = 1
        params.TOKEN_UUID = this.selectedPaymentToken
      }
      
      // Add payment method if multiple methods are available or if not using card
      if (this.availablePaymentMethods.length > 1 || this.paymentMethod !== 'card') {
        params.PAYMENT_METHOD = this.paymentMethod // 'card' or 'bankaccount'
      }

      this.$transactionApi.post("/checkout/protokol/order", this.billingAddress, {
        params: params
      }).then(({data}) => {
        this.$gtag.purchase({
          transaction_id: this.LICENSE_CODE,
          affiliation: "Checkout",
          value: this.TOTAL_AMOUNT
        })

        let checkoutPage = new BroadcastChannel('checkout')
        checkoutPage.postMessage({
          type: "redirected_to_payment"
        })

        if (data.paymentLink) {
          window.location.href = data.paymentLink
        } else {
          this.$router.push('/checkout/success')
        }
      }).catch((err) => {
        let message = err.response && err.response.data && err.response.data.message ? err.response.data.message : this.$t('checkout.errorMessage')
        this.$mainDialog.msgBoxOk(message, {
          okOnly: true,
        })
      }).finally(() => {
        popup.hide()
      })
    }
  },
  async mounted() {
    this.$gtag.event('checkout_started')

    let query = this.$route.query;
    this.PRODS = query.PRODS;
    this.LICENSE_CODE = query.LICENSE_CODE;
    this.PROD_NAMES = query.PROD_NAMES;
    this.GROUPS = query.GROUPS;
    this.PRICES = query.PRICES;
    this.TPERIOD = query.TPERIOD;
    this.QTY = query.QTY;
    this.PHASH = query.PHASH;
    this.TPERIOD = query.TPERIOD
    this.PROD_OPTIONS = query.OPTIONS
    this.HUMAN_TOTAL_AMOUNT = query.TOTAL_AMOUNT ? (parseFloat(query.TOTAL_AMOUNT) / 100).toFixed(2) : ''
    this.PAYMENT_LINK = query.PAYMENT_LINK
    this.CURRENCY = query.CURRENCY
    this.IS_PRORATED = query.IS_PRORATED === '1' || query.IS_PRORATED === 'true'

    this.prodNames = this.PROD_NAMES.split(',').map(s => s.trim());
    this.quantities = this.QTY.split(',').map(s => s.trim());
    this.prices = (query.PRICES || '').split(',').map(s => (parseFloat(s) / 100).toFixed(2));
    this.prodOptions = this.PROD_OPTIONS.split(",").map(s => s.trim());

    let result = query.QTY.split(',').map(s => s.trim()).map(function(value, index) {
      return [query.QTY.split(',').map(s => s.trim())[index], query.PRICES.split(',').map(s => s.trim())[index]];
    });

    for (let i = 0; i < result.length; i++) {
      this.total += parseInt(result[i][0]) * parseInt(result[i][1]);
    }

    this.$transactionApi.get("/payment-tokens").then(({data}) => {
      this.paymentTokens = data.tokens || data || []
      if (this.paymentTokens.length > 0) {
        this.selectedPaymentToken = this.paymentTokens[0].uuid
      }
    })

    // Fetch project info to check allow_bank_payment and get primary_payment_method
    await this.$store.dispatch('fetchProjectInfo')
    
    const projectInfo = this.$store.getters.getProjectInfo
    
    // Set initial payment method based on available methods
    if (this.availablePaymentMethods.length === 1) {
      // Only one method available, use it
      this.paymentMethod = this.availablePaymentMethods[0]
    } else if (projectInfo && projectInfo.primary_payment_method && this.availablePaymentMethods.includes(projectInfo.primary_payment_method)) {
      // Pre-select the user's preferred payment method if it's available
      this.paymentMethod = projectInfo.primary_payment_method
    } else if (this.isCardAvailable) {
      // Default to card if available
      this.paymentMethod = 'card'
    } else {
      // Otherwise use the first available method
      this.paymentMethod = this.availablePaymentMethods[0]
    }
  }
}
</script>